/* Handelspartner globe — MapLibre GL (vendored), Natural Earth countries,
   packed-glyph protocol (Shopify assets are flat, so {range}.pbf lives inside
   wp-map-glyphs.json). List/filter coupling comes from the section script
   contract: setVisibleHandles(), focusPartner(). */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class WpPartnerMap extends HTMLElement {
    connectedCallback() {
      this.style.setProperty('--wp-hpo-texture', `url(${this.dataset.texture})`);
      this._partners = JSON.parse(document.getElementById('wp-hpo-data').textContent)
        .map((p) => ({ ...p, lat: Number(p.lat), lng: Number(p.lng) }));
      spreadSharedCoords(this._partners);
      this._visible = null;
      this._io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this._io.disconnect(); this._init(); }
      }, { rootMargin: '200px' });
      this._io.observe(this);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this.map?.remove();
      this.map = null;
    }

    async _init() {
      if (!window.maplibregl) {
        this._retries = (this._retries || 0) + 1;
        if (this._retries > 50) {
          console.warn('wp-partner-map: maplibre-gl not available, map skipped');
          return;
        }
        setTimeout(() => this._init(), 100);
        return;
      }
      let glyphs;
      try {
        glyphs = await (await fetch(this.dataset.glyphs)).json();
      } catch (err) {
        console.warn('wp-partner-map: glyph pack failed to load, map skipped', err);
        return;
      }
      maplibregl.addProtocol('wpglyphs', async ({ url }) => {
        const range = url.match(/(\d+-\d+)\.pbf$/)?.[1];
        const b64 = glyphs[range];
        // missing range → throw so MapLibre's own fallback (tinySDF + warn) fires
        if (!b64) throw new Error('wpglyphs: range ' + range + ' not packed');
        const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
        return { data: bytes.buffer };
      });

      const map = (this.map = new maplibregl.Map({
        container: this.querySelector('[data-map-container]'),
        style: {
          version: 8,
          projection: { type: 'globe' },
          glyphs: 'wpglyphs://{fontstack}/{range}.pbf',
          sources: {
            world: { type: 'geojson', data: { type: 'Feature', geometry: { type: 'Polygon',
              coordinates: [[[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]]] } } },
            countries: { type: 'geojson', data: this.dataset.countries },
            partners: { type: 'geojson', data: this._geojson() },
          },
          layers: [
            // ocean: drawn on the sphere only — space stays transparent so the
            // fixed panel texture shows through and never moves with zoom
            { id: 'ocean', type: 'fill', source: 'world', paint: { 'fill-color': '#d9cfae' } },
            { id: 'land', type: 'fill', source: 'countries', paint: { 'fill-color': '#f4f0da' } },
            { id: 'borders', type: 'line', source: 'countries',
              paint: { 'line-color': '#8a6a4f', 'line-width': 0.6, 'line-opacity': 0.7 } },
            { id: 'country-labels', type: 'symbol', source: 'countries',
              layout: { 'text-field': ['get', 'NAME_DE'], 'text-font': ['Noto Sans Regular'],
                        'text-size': 11, 'text-transform': 'none' },
              paint: { 'text-color': '#6d5138', 'text-halo-color': '#f4f0da', 'text-halo-width': 1 } },
            { id: 'partner-pins', type: 'symbol', source: 'partners',
              layout: { 'icon-image': 'wp-pin', 'icon-size': 0.75, 'icon-anchor': 'bottom',
                        'icon-allow-overlap': true } },
          ],
        },
        center: [-20, 15],
        zoom: 1.6,
        minZoom: 1, maxZoom: 6,
        attributionControl: false,
        keyboard: false,
      }));
      map.getCanvas().setAttribute('tabindex', '-1');

      const pin = new Image(44, 56);
      pin.onload = () => map.addImage('wp-pin', pin, { pixelRatio: 1 });
      pin.src = this.dataset.pin;

      map.on('click', 'partner-pins', (e) => {
        const f = e.features[0];
        this._popup(f.properties, f.geometry.coordinates);
      });
      map.on('mouseenter', 'partner-pins', () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', 'partner-pins', () => (map.getCanvas().style.cursor = ''));
    }

    _geojson() {
      const list = this._visible
        ? this._partners.filter((p) => this._visible.includes(p.handle))
        : this._partners;
      return { type: 'FeatureCollection', features: list.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        properties: { handle: p.handle, name: p.name, city: p.city, country: p.country, url: p.url },
      })) };
    }

    setVisibleHandles(handles) {
      this._visible = handles;
      if (this.map?.getSource('partners')) this.map.getSource('partners').setData(this._geojson());
    }

    focusPartner(handle) {
      const p = this._partners.find((x) => x.handle === handle);
      if (!p || !this.map) return;
      const move = { center: [p.lng, p.lat], zoom: 4.5 };
      REDUCED ? this.map.jumpTo(move) : this.map.flyTo({ ...move, duration: 1400 });
      this._popup(p, [p.lng, p.lat]);
    }

    _popup(p, coords) {
      this._activePopup?.remove();
      this._activePopup = new maplibregl.Popup({ className: 'wp-hpo-popup', offset: 40 })
        .setLngLat(coords)
        .setHTML(
          `<p class="wp-hpo-popup__name">${esc(p.name)}</p>
           <p class="wp-hpo-popup__meta">${p.city ? esc(p.city) + ' · ' : ''}${esc(p.country)}</p>
           <a class="wp-hpo-popup__link" href="${esc(p.url)}">Mehr erfahren →</a>`,
        )
        .addTo(this.map);
    }
  }

  // partners sharing rounded coords fan out horizontally so pins never stack
  function spreadSharedCoords(partners) {
    const groups = new Map();
    for (const p of partners) {
      const k = `${p.lat.toFixed(1)},${p.lng.toFixed(1)}`;
      (groups.get(k) ?? groups.set(k, []).get(k)).push(p);
    }
    for (const g of groups.values()) {
      if (g.length < 2) continue;
      g.forEach((p, i) => { p.lng += (i - (g.length - 1) / 2) * 0.5; });
    }
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  customElements.define('wp-partner-map', WpPartnerMap);

  // ── list / filter / search controller ────────────────────────────────
  function setupListController() {
    const mapEl = document.querySelector('wp-partner-map');
    const list = document.getElementById('wp-hpo-list');
    const select = document.getElementById('wp-hpo-continent');
    const search = document.getElementById('wp-hpo-search');
    if (!mapEl || !list || !select || !search) return;
    const cards = [...list.querySelectorAll('.wp-hpo__card')];
    const empty = list.querySelector('.wp-hpo__empty');

    function apply() {
      const cont = select.value;
      const q = search.value.trim().toLowerCase();
      const visible = [];
      for (const card of cards) {
        const ok = (!cont || card.dataset.continent === cont) &&
                   (!q || card.dataset.search.includes(q));
        card.hidden = !ok;
        if (ok) visible.push(card.dataset.handle);
      }
      empty.hidden = visible.length > 0;
      mapEl.setVisibleHandles(cont || q ? visible : null);
    }
    select.addEventListener('change', apply);
    search.addEventListener('input', apply);

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-map-jump]');
      if (!btn) return;
      mapEl.focusPartner(btn.closest('.wp-hpo__card').dataset.handle);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupListController);
  } else {
    setupListController();
  }
})();
